import { ComponentTypes } from "../Components/ComponentFactory/types"
import { MdArrowForward, MdCheck, MdCable, MdOutlineElderly, MdOutlineFilterAlt, MdOutlinePercent, MdStraighten, MdOutlineFileUpload, MdOutlineFileDownload } from "react-icons/md";
import { RiEqualFill } from "react-icons/ri";
import SpliceSvgIcon from "../Components/Icons/SpliceIcon";
export enum partsTypes {
    TRANSCEIVERS = "transceivers",
    ONTS = "onts",
    SPLITTERS = "splitters"
}

export enum lossTypes {
    SPLITTERS_LOSS = "splitter_loss",
    FIBER_LOSS = "fiber_loss"
}

export interface TransceiversI {
    id: string,
    value: string,
    name: string,
    summary: string,
    xgspon_min_dl_tx: number,
    xgspon_min_dl_rx: number,
    gpon_min_dl_tx: number,
    gpon_min_ul_rx: number,
    model_number: string,
}

export enum TRANSCEIVERS_TYPES {
    COMBO_PON = "combo_pon",
    XGS_PON = "xgs_pon",
    GPON = "gpon"
}

export enum ONTS_TYPES {
    SGX00 = "SGX00",
    SGT00 = "SGT00",
    SXX00 = "SXX00",
    SXT00 = "SXT00"
}

export enum SPLITTERS_TYPES {
    ONEx2 = "1x2",
    ONEx3 = "1x3",
    ONEx4 = "1x4",
    ONEx6 = "1x6",
    ONEx8 = "1x8",
    ONEx9 = "1x9",
    ONEx12 = "1x12",
    ONEx16 = "1x16",
    ONEx24 = "1x24",
    ONEx32 = "1x32",
    ONEx64 = "1x64",
    ONEx128 = "1x128",
    ONEx256 = "1x256",
}

export interface ONTSI {
    id: string,
    name: string,
    summary: string,
    min_dl_tx: number,
    min_dl_rx: number,
    protocol: string
}

export interface SPLITTERSI {
    id: string,
    value: string,
    name: string,
    split_count: number | null,
    insertion_loss: number,
    uniformity_loss: number,
    polarization_dependent_loss: number,
    db: number
}

export interface SPLITTERS_LOSSI {
    id: string,
    loss: number
}

export interface FIBER_LOSSI {
    id: string,
    technology: string,
    direction: string,
    light_freq: string,
    loss_per_km: number
}

export interface HardwareI {
    [partsTypes.TRANSCEIVERS]: {
        [key: string]: TransceiversI
    },
    [partsTypes.ONTS]: {
        [key: string]: ONTSI
    },
    [partsTypes.SPLITTERS]: {
        [key: string]: SPLITTERSI
    },
}

export interface LossI {
    [lossTypes.SPLITTERS_LOSS]: SPLITTERS_LOSSI[],
    [lossTypes.FIBER_LOSS]: {
        [key: string]: FIBER_LOSSI
    }
}

export enum DISTANCE_MEASURES {
    KM = "km",
    MIL = "mi"
}


export interface distanceMeasuresI {
    [key: string]: {
        id: string,
        value: string,
        name: string
    }
}

export interface additionalCalcParamsI {
    [key: string]: {
        defVal: number | string
    }
}

export interface CommonSettingsI {
    hardware: HardwareI,
    loss: LossI,
    additional_calc_params: additionalCalcParamsI,
    distance_measures: distanceMeasuresI
}

export enum addditionalCalcParams {
    SPLICE_QTY = "splice_qty",
    AGING_MARGIN = "aging_margin",
    CONNECTORS = "connectors",
    TOTAL_DISTANCE = "total_distance",
    TOTAL_DISTANCE_MEASURE = "total_distance_measure",
    MARGIN_REQUIREMENT = "margin_requirement",
    MEASUREMENT_ERROR_GAIN_LOSS = "measurement_error_gain_loss",
    CONNECTOR_LOSS = "connector_loss",
    SPLICE_LOSS = "splice_loss"
}

export const splitterIDs = [2, 3, 4, 6, 8, 9, 12, 16, 24, 32, 64, 128, 256]
export const splitterLoss = [5.6, 6.7, 8.5, 9.7, 11.8, 17.3, 14.1, 15.2, 18.9, 18.7, 22.3, 26, 29.8]

export const CommonSettings: CommonSettingsI = {
    hardware: {
        [partsTypes.TRANSCEIVERS]: {
            [TRANSCEIVERS_TYPES.COMBO_PON]: {
                id: TRANSCEIVERS_TYPES.COMBO_PON,
                value: TRANSCEIVERS_TYPES.COMBO_PON,
                name: "Combo PON",
                summary: "COMBO SFP Class C+/N2",
                xgspon_min_dl_tx: 4,
                xgspon_min_dl_rx: -30,
                gpon_min_dl_tx: 3,
                gpon_min_ul_rx: -30,
                model_number: 'POMC1SFP-H1'
            },
            [TRANSCEIVERS_TYPES.XGS_PON]: {
                id: TRANSCEIVERS_TYPES.XGS_PON,
                value: TRANSCEIVERS_TYPES.XGS_PON,
                name: "XGS-PON",
                summary: "XGS-PON Class N2",
                xgspon_min_dl_tx: 4,
                xgspon_min_dl_rx: -28,
                gpon_min_dl_tx: -100,
                gpon_min_ul_rx: -100,
                model_number: 'POMX2SFP-H1'
            },
            [TRANSCEIVERS_TYPES.GPON]: {
                id: TRANSCEIVERS_TYPES.GPON,
                value: TRANSCEIVERS_TYPES.GPON,
                name: "GPON",
                summary: "GPON Class C+",
                xgspon_min_dl_tx: -100,
                xgspon_min_dl_rx: -100,
                gpon_min_dl_tx: 3,
                gpon_min_ul_rx: -30,
                model_number: 'POMG5SFP'
            }
        },
        [partsTypes.ONTS]: {
            [ONTS_TYPES.SGX00]: {
                id: ONTS_TYPES.SGX00,
                name: ONTS_TYPES.SGX00,
                summary: "Gigabit SGX00 Indoor ONT",
                min_dl_tx: 0.5,
                min_dl_rx: -28,
                protocol: "GPON"
            },
            [ONTS_TYPES.SGT00]: {
                id: ONTS_TYPES.SGT00,
                name: ONTS_TYPES.SGT00,
                summary: "Gigabit SGT00 Outdoor ONT",
                min_dl_tx: 0.5,
                min_dl_rx: -28,
                protocol: "GPON"
            },
            [ONTS_TYPES.SXX00]: {
                id: ONTS_TYPES.SXX00,
                name: ONTS_TYPES.SXX00,
                summary: "Multi-gigabit SXX00 Indoor ONT",
                min_dl_tx: 4,
                min_dl_rx: -28,
                protocol: "XGS-PON"
            },
            [ONTS_TYPES.SXT00]: {
                id: ONTS_TYPES.SXT00,
                name: ONTS_TYPES.SXT00,
                summary: "Multi-gigabit SXT00 Outdoor ONT",
                min_dl_tx: 4,
                min_dl_rx: -28,
                protocol: "XGS-PON"
            }
        },
        [partsTypes.SPLITTERS]: {
            [SPLITTERS_TYPES.ONEx2]: {
                id: SPLITTERS_TYPES.ONEx2,
                value: SPLITTERS_TYPES.ONEx2,
                name: "1x2",
                split_count: 1,
                insertion_loss: 4.6,
                uniformity_loss: 0.5,
                polarization_dependent_loss: 0.3,
                db: 5.40
            },
            [SPLITTERS_TYPES.ONEx3]: {
                id: SPLITTERS_TYPES.ONEx3,
                value: SPLITTERS_TYPES.ONEx3,
                name: "1x3",
                split_count: null,
                insertion_loss: 6,
                uniformity_loss: 0.45,
                polarization_dependent_loss: 0.2,
                db: 6.65
            },
            [SPLITTERS_TYPES.ONEx4]: {
                id: SPLITTERS_TYPES.ONEx4,
                value: SPLITTERS_TYPES.ONEx4,
                name: "1x4",
                split_count: 2,
                insertion_loss: 7.6,
                uniformity_loss: 0.6,
                polarization_dependent_loss: 0.3,
                db: 8.50
            },
            [SPLITTERS_TYPES.ONEx6]: {
                id: SPLITTERS_TYPES.ONEx6,
                value: SPLITTERS_TYPES.ONEx6,
                name: "1x6",
                split_count: null,
                insertion_loss: 9,
                uniformity_loss: 0.55,
                polarization_dependent_loss: 0.1,
                db: 9.65
            },
            [SPLITTERS_TYPES.ONEx8]: {
                id: SPLITTERS_TYPES.ONEx8,
                value: SPLITTERS_TYPES.ONEx8,
                name: "1x8",
                split_count: 3,
                insertion_loss: 10.7,
                uniformity_loss: 0.8,
                polarization_dependent_loss: 0.3,
                db: 11.80
            },
            [SPLITTERS_TYPES.ONEx9]: {
                id: SPLITTERS_TYPES.ONEx9,
                value: SPLITTERS_TYPES.ONEx9,
                name: "1x9",
                split_count: null,
                insertion_loss: 16.5,
                uniformity_loss: 0.5,
                polarization_dependent_loss: 0.3,
                db: 17.30
            },
            [SPLITTERS_TYPES.ONEx12]: {
                id: SPLITTERS_TYPES.ONEx12,
                value: SPLITTERS_TYPES.ONEx12,
                name: "1x12",
                split_count: null,
                insertion_loss: 12.5,
                uniformity_loss: 1.2,
                polarization_dependent_loss: 0.4,
                db: 14.10
            },
            [SPLITTERS_TYPES.ONEx16]: {
                id: SPLITTERS_TYPES.ONEx16,
                value: SPLITTERS_TYPES.ONEx16,
                name: "1x16",
                split_count: 4,
                insertion_loss: 13.8,
                uniformity_loss: 1.1,
                polarization_dependent_loss: 0.3,
                db: 15.20
            },
            [SPLITTERS_TYPES.ONEx24]: {
                id: SPLITTERS_TYPES.ONEx24,
                value: SPLITTERS_TYPES.ONEx24,
                name: "1x24",
                split_count: null,
                insertion_loss: 16,
                uniformity_loss: 2.5,
                polarization_dependent_loss: 0.4,
                db: 18.90
            },
            [SPLITTERS_TYPES.ONEx32]: {
                id: SPLITTERS_TYPES.ONEx32,
                value: SPLITTERS_TYPES.ONEx32,
                name: "1x32",
                split_count: 5,
                insertion_loss: 16.9,
                uniformity_loss: 1.5,
                polarization_dependent_loss: 0.3,
                db: 18.70
            },
            [SPLITTERS_TYPES.ONEx64]: {
                id: SPLITTERS_TYPES.ONEx64,
                value: SPLITTERS_TYPES.ONEx64,
                name: "1x64",
                split_count: 6,
                insertion_loss: 20,
                uniformity_loss: 2,
                polarization_dependent_loss: 0.3,
                db: 22.30
            },
            [SPLITTERS_TYPES.ONEx128]: {
                id: SPLITTERS_TYPES.ONEx128,
                value: SPLITTERS_TYPES.ONEx128,
                name: "1x128",
                split_count: 7,
                insertion_loss: 23.1,
                uniformity_loss: 2.6,
                polarization_dependent_loss: 0.3,
                db: 26.00
            },
            [SPLITTERS_TYPES.ONEx256]: {
                id: SPLITTERS_TYPES.ONEx256,
                value: SPLITTERS_TYPES.ONEx256,
                name: "1x256",
                split_count: 8,
                insertion_loss: 26.2,
                uniformity_loss: 3.3,
                polarization_dependent_loss: 0.3,
                db: 29.80
            },
        }
    },
    loss: {
        [lossTypes.SPLITTERS_LOSS]: splitterIDs.map((num: number, i: number) => {
            return {
                id: `1x${num}`,
                loss: splitterLoss[i] // dB
            }
        }),
        [lossTypes.FIBER_LOSS]: {
            "xgs_pon_upstream": {
                id: "xgs_pon_upstream",
                technology: 'XGS-PON',
                direction: 'Upstream',
                light_freq: '1270-1280 nm',
                loss_per_km: 0.35 // db/km
            },
            "xgs_pon_downstream": {
                id: "xgs_pon_downstream",
                technology: 'XGS-PON',
                direction: 'Downstream',
                light_freq: '1575-1580 nm',
                loss_per_km: 0.25 // db/km
            },
            "gpon_upstream": {
                id: "gpon_upstream",
                technology: 'GPON',
                direction: 'Upstream',
                light_freq: '1310 nm',
                loss_per_km: 0.35 // db/km
            },
            "gpon_downstream": {
                id: "gpon_downstream",
                technology: 'GPON',
                direction: 'Downstream',
                light_freq: '1490 nm',
                loss_per_km: 0.25 // db/km
            }
        }
    },
    additional_calc_params: {
        [addditionalCalcParams.SPLICE_QTY]: {
            defVal: 1
        },
        [addditionalCalcParams.AGING_MARGIN]: {
            defVal: 0.0
        },
        [addditionalCalcParams.CONNECTORS]: {
            defVal: 4
        },
        [addditionalCalcParams.TOTAL_DISTANCE]: {
            defVal: 4
        },
        [addditionalCalcParams.TOTAL_DISTANCE_MEASURE]: {
            defVal: DISTANCE_MEASURES.KM
        },
        [addditionalCalcParams.MARGIN_REQUIREMENT]: {
            defVal: 1.0
        },
        [addditionalCalcParams.MEASUREMENT_ERROR_GAIN_LOSS]: {
            defVal: 0.4
        },
        [addditionalCalcParams.CONNECTOR_LOSS]: {
            defVal: 0.75
        },
        [addditionalCalcParams.SPLICE_LOSS]: {
            defVal: 0.05
        },
    },
    distance_measures: {
        [DISTANCE_MEASURES.KM]: {
            id: DISTANCE_MEASURES.KM,
            name: DISTANCE_MEASURES.KM,
            value: DISTANCE_MEASURES.KM
        },
        [DISTANCE_MEASURES.MIL]: {
            id: DISTANCE_MEASURES.MIL,
            name: DISTANCE_MEASURES.MIL,
            value: DISTANCE_MEASURES.MIL
        },
    }
}


export const imagesList = {
    "Transceiver": {
        type: ComponentTypes.ICON,
        dummy: true,
        icons: [MdArrowForward],
        name: "Transceiver",
        width: 20
    },
    "Splitter": {
        type: ComponentTypes.ICON,
        dummy: true,
        icons: [MdOutlineFilterAlt],
        style: [{transform: 'rotate(90deg)'}],
        name: "Splitter",
        width: 20
    },
    "SpliceQTY": {
        type: ComponentTypes.ICON,
        dummy: true,
        icons: [SpliceSvgIcon],
        name: "SpliceQTY",
        width: 20
    },
    "Aging": {
        type: ComponentTypes.ICON,
        dummy: true,
        icons: [MdOutlineElderly],
        name: "Aging",
        width: 20
    },
    "Connectors": {
        type: ComponentTypes.ICON,
        dummy: true,
        icons: [MdCable],
        name: "Connectors",
        width: 20
    },
    "Measure": {
        type: ComponentTypes.ICON,
        dummy: true,
        icons: [MdOutlinePercent],
        name: "Measure",
        width: 20
    },
    "Distance": {
        type: ComponentTypes.ICON,
        dummy: true,
        icons: [MdStraighten],
        name: "Distance",
        width: 20
    },
    "Result": {
        type: ComponentTypes.ICON,
        dummy: true,
        icons: [RiEqualFill],
        name: "Result",
        width: 20
    },
    "MarginReq": {
        type: ComponentTypes.ICON,
        dummy: true,
        icons: [MdCheck],
        name: "MarginReq",
        width: 20
    },
    "UplinkIcon": {
        type: ComponentTypes.ICON,
        dummy: true,
        icons: [MdOutlineFileUpload],
        name: "UplinkIcon",
        width: 20
    },
    "DownlinkIcon": {
        type: ComponentTypes.ICON,
        dummy: true,
        icons: [MdOutlineFileDownload],
        name: "DownlinkIcon",
        width: 20
    },
}